import api from './api';

/**
 * SERVICE DE CONFIGURATION FRONTEND - KRILLOU
 * ============================================
 * 
 * Service pour récupérer les paramètres de configuration depuis la BDD
 * via l'API backend (remplace les valeurs codées en dur dans pricingConfig.js)
 */
class ConfigService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Récupère la configuration financière (commission, TVA) depuis la BDD
   * @returns {Promise<Object>} Configuration financière
   */
  async getFinancialConfig() {
    const cacheKey = 'financial_config';
    
    // Vérifier le cache
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      console.log('📋 [ConfigService] Using cached financial config');
      return cached;
    }

    try {
      console.log('🔄 [ConfigService] Fetching financial config from database...');
      const response = await api.get('/config/financial');
      
      if (response.data.success) {
        const config = response.data.data;
        
        // Mettre en cache
        this.setCachedData(cacheKey, config);
        
        console.log('✅ [ConfigService] Financial config loaded from database:', {
          commission_rate: config.commission.rate_percent,
          vat_countries: Object.keys(config.vat.rates_by_country).length
        });
        
        return config;
      } else {
        throw new Error('Failed to fetch financial config');
      }
    } catch (error) {
      console.error('❌ [ConfigService] Failed to fetch financial config:', error);
      
      // Fallback sur configuration par défaut
      console.warn('🔄 [ConfigService] Using fallback financial config');
      return this.getFallbackFinancialConfig();
    }
  }

  /**
   * Récupère la configuration de pricing (limites, arrondis, etc.) depuis la BDD
   * @returns {Promise<Object>} Configuration pricing
   */
  async getPricingConfig() {
    const cacheKey = 'pricing_config';
    
    // Vérifier le cache
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      console.log('📋 [ConfigService] Using cached pricing config');
      return cached;
    }

    try {
      console.log('🔄 [ConfigService] Fetching pricing config from database...');
      const response = await api.get('/config/pricing');
      
      if (response.data.success) {
        const config = response.data.data;
        
        // Mettre en cache
        this.setCachedData(cacheKey, config);
        
        console.log('✅ [ConfigService] Pricing config loaded from database');
        
        return config;
      } else {
        throw new Error('Failed to fetch pricing config');
      }
    } catch (error) {
      console.error('❌ [ConfigService] Failed to fetch pricing config:', error);
      
      // Fallback sur configuration par défaut
      console.warn('🔄 [ConfigService] Using fallback pricing config');
      return this.getFallbackPricingConfig();
    }
  }

  /**
   * Récupère la configuration complète (financial + pricing)
   * @returns {Promise<Object>} Configuration complète
   */
  async getCompleteConfig() {
    try {
      const [financial, pricing] = await Promise.all([
        this.getFinancialConfig(),
        this.getPricingConfig()
      ]);

      return {
        financial,
        pricing,
        retrieved_at: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ [ConfigService] Failed to fetch complete config:', error);
      throw error;
    }
  }

  /**
   * Vide le cache (utile pour forcer un rechargement)
   */
  clearCache() {
    this.cache.clear();
    console.log('🗑️ [ConfigService] Cache cleared');
  }

  /**
   * Récupère des données depuis le cache si elles sont valides
   * @private
   */
  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  /**
   * Met des données en cache
   * @private
   */
  setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Configuration financière de fallback (si BDD indisponible)
   * @private
   */
  getFallbackFinancialConfig() {
    return {
      commission: {
        rate_percent: 8,
        applied_on: 'total_amount'
      },
      vat: {
        rates_by_country: {
          'DEFAULT': 20,
          'FR': 20,
          'DZ': 19,
          'MA': 20,
          'TN': 19
        },
        applied_on: 'commission'
      },
      general: {
        currency_default: 'EUR',
        calculation_method: 'ttc_with_internal_deductions'
      },
      fallback: true
    };
  }

  /**
   * Configuration pricing de fallback (si BDD indisponible)
   * @private
   */
  getFallbackPricingConfig() {
    return {
      limits: {
        min_rental_days: 1,
        max_rental_days: 365,
        min_rental_hours: 4
      },
      deposit: {
        calculation_method: 'listing',
        default_percent: 30,
        min_amount: 50,
        max_amount: 2000
      },
      rounding: {
        round_to_cents: true,
        round_method: 'ceil'
      },
      discounts: {
        weekly_discount_percent: 5,
        monthly_discount_percent: 10
      },
      fallback: true
    };
  }
}

export default new ConfigService();
